import React from 'react';
import {
    Box,
    Chip,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
    Typography
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import FolderRoundedIcon from '@mui/icons-material/FolderRounded';
import ImageRoundedIcon from '@mui/icons-material/ImageRounded';
import InsertDriveFileRoundedIcon from '@mui/icons-material/InsertDriveFileRounded';
import PictureAsPdfRoundedIcon from '@mui/icons-material/PictureAsPdfRounded';
import VisibilityIcon from '@mui/icons-material/Visibility';

function formatBytes(value) {
    const bytes = Number(value || 0);
    if (!bytes) return 'Size unavailable';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function FileTypeIcon({ contentType }) {
    const type = String(contentType || '').toLowerCase();
    if (type === 'application/pdf') return <PictureAsPdfRoundedIcon />;
    if (type.startsWith('image/')) return <ImageRoundedIcon />;
    return <InsertDriveFileRoundedIcon />;
}

const SECTION_LABELS = {
    section_3: 'Site & scheme',
    section_4: 'Planning',
    section_5: 'Surveys',
    section_6: 'Development',
    section_7: 'Costs',
    section_8: 'Programme',
    section_9: 'Procurement',
    section_10: 'Experience',
    section_11: 'Professional team'
};

function getPathSegments(file) {
    return String(file?.virtual_path || '').split('/').filter(Boolean);
}

export function getRepositoryDocumentMeta(file) {
    const source = String(file?.source || '').toLowerCase();
    const segments = getPathSegments(file);
    const section = segments.find((segment) => /^section_\d+$/.test(segment));
    const stage = segments.find((segment) => segment === 'initial-report' || segment.startsWith('interim-'));
    const sectionLabel = SECTION_LABELS[section] || (section ? section.replace(/_/g, ' ') : 'Questionnaire');
    const stageLabel = stage === 'initial-report'
        ? 'Initial Report'
        : stage?.startsWith('interim-')
            ? `Interim Report ${stage.replace('interim-', '')}`
            : 'Questionnaire';

    if (source === 'questionnaire') {
        return {
            domain: sectionLabel,
            relationship: `${stageLabel} evidence`,
            status: 'Linked',
            color: 'success',
            detail: `${file.name} is linked as ${sectionLabel.toLowerCase()} evidence for the ${stageLabel}.`
        };
    }
    if (source === 'issued') {
        return {
            domain: 'Issued report',
            relationship: 'Generated document',
            status: 'Issued',
            color: 'info',
            detail: `${file.name} is retained as an issued project report.`
        };
    }
    return {
        domain: 'Project upload',
        relationship: 'No information link',
        status: 'Uploaded',
        color: 'warning',
        detail: `${file.name} is stored in Project uploads and is not linked to structured project information.`
    };
}

export default function ProjectRepositoryGrid({ folders = [], files = [], busyItemId = '', selectedItemId = '', onOpenFolder, onSelect, onPreview, onDownload }) {
    if (!folders.length && !files.length) {
        return (
            <Box sx={{ py: 7, textAlign: 'center', color: 'text.secondary' }}>
                <InsertDriveFileRoundedIcon sx={{ fontSize: 42, opacity: 0.45 }} />
                <Typography sx={{ mt: 1, fontWeight: 800 }}>This folder is empty</Typography>
            </Box>
        );
    }

    return (
        <TableContainer sx={{ width: '100%' }}>
            <Table size="small" aria-label="Project documents" sx={{ tableLayout: 'fixed' }}>
                <TableHead>
                    <TableRow>
                        <TableCell sx={{ width: { xs: 'auto', md: '42%' }, fontSize: 11, fontWeight: 850, color: 'text.secondary' }}>Document</TableCell>
                        <TableCell sx={{ display: { xs: 'none', md: 'table-cell' }, width: '18%', fontSize: 11, fontWeight: 850, color: 'text.secondary' }}>Domain</TableCell>
                        <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' }, width: '22%', fontSize: 11, fontWeight: 850, color: 'text.secondary' }}>Information links</TableCell>
                        <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' }, width: 112, fontSize: 11, fontWeight: 850, color: 'text.secondary' }}>Status</TableCell>
                        <TableCell align="right" sx={{ display: { xs: 'none', sm: 'table-cell' }, width: 84, fontSize: 11, fontWeight: 850, color: 'text.secondary' }}>Actions</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {folders.map((folder) => (
                        <TableRow key={folder.path} hover>
                            <TableCell sx={{ py: 1.3 }}>
                                <Box
                                    component="button"
                                    type="button"
                                    onClick={() => onOpenFolder(folder.path)}
                                    sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, p: 0, border: 0, bgcolor: 'transparent', color: 'text.primary', cursor: 'pointer', textAlign: 'left' }}
                                >
                                    <FolderRoundedIcon color="primary" />
                                    <Box>
                                        <Typography sx={{ fontSize: 13, fontWeight: 850 }}>{folder.name}</Typography>
                                        <Typography variant="caption" color="text.secondary">Open collection</Typography>
                                    </Box>
                                </Box>
                            </TableCell>
                            <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }} />
                            <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }} />
                            <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }} />
                            <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }} />
                        </TableRow>
                    ))}
                    {files.map((file) => {
                        const meta = getRepositoryDocumentMeta(file);
                        const selected = file.item_id === selectedItemId;
                        return (
                            <TableRow key={file.item_id} hover selected={selected} onClick={() => onSelect?.(file)} sx={{ cursor: 'pointer' }}>
                                <TableCell sx={{ py: 1.25 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, minWidth: 0 }}>
                                        <Box sx={{ mt: 0.1, color: file.content_type === 'application/pdf' ? 'error.main' : 'primary.main' }}>
                                            <FileTypeIcon contentType={file.content_type} />
                                        </Box>
                                        <Box sx={{ minWidth: 0 }}>
                                            <Typography title={file.name} sx={{ fontSize: 13, fontWeight: 800, overflowWrap: 'anywhere', lineHeight: 1.25 }}>{file.name}</Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {formatBytes(file.size_bytes)}{file.date ? ` · ${new Date(file.date).toLocaleDateString('en-GB')}` : ''}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary" sx={{ display: { xs: 'block', sm: 'none' }, mt: 0.35 }}>
                                                {meta.domain} · {meta.relationship}
                                            </Typography>
                                            <Box sx={{ display: { xs: 'flex', sm: 'none' }, alignItems: 'center', justifyContent: 'space-between', gap: 1, mt: 0.75 }}>
                                                <Chip size="small" label={meta.status} color={meta.color} variant="outlined" sx={{ height: 24, fontSize: 11, fontWeight: 800 }} />
                                                <Box sx={{ display: 'flex', gap: 0.25 }}>
                                                    {file.previewable ? (
                                                        <IconButton size="small" aria-label={`Preview ${file.name}`} disabled={busyItemId === file.item_id} onClick={(event) => { event.stopPropagation(); onPreview(file); }}>
                                                            <VisibilityIcon fontSize="small" />
                                                        </IconButton>
                                                    ) : null}
                                                    <IconButton size="small" aria-label={`Download ${file.name}`} disabled={busyItemId === file.item_id} onClick={(event) => { event.stopPropagation(); onDownload(file); }}>
                                                        <DownloadIcon fontSize="small" />
                                                    </IconButton>
                                                </Box>
                                            </Box>
                                        </Box>
                                    </Box>
                                </TableCell>
                                <TableCell sx={{ display: { xs: 'none', md: 'table-cell' }, fontSize: 12 }}>{meta.domain}</TableCell>
                                <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' }, fontSize: 12 }}>{meta.relationship}</TableCell>
                                <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}><Chip size="small" label={meta.status} color={meta.color} variant="outlined" sx={{ height: 24, fontSize: 11, fontWeight: 800 }} /></TableCell>
                                <TableCell align="right" sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.25 }}>
                                        {file.previewable ? (
                                            <Tooltip title="Preview">
                                                <span>
                                                    <IconButton size="small" aria-label={`Preview ${file.name}`} disabled={busyItemId === file.item_id} onClick={(event) => { event.stopPropagation(); onPreview(file); }}>
                                                        <VisibilityIcon fontSize="small" />
                                                    </IconButton>
                                                </span>
                                            </Tooltip>
                                        ) : null}
                                        <Tooltip title="Download">
                                            <span>
                                                <IconButton size="small" aria-label={`Download ${file.name}`} disabled={busyItemId === file.item_id} onClick={(event) => { event.stopPropagation(); onDownload(file); }}>
                                                    <DownloadIcon fontSize="small" />
                                                </IconButton>
                                            </span>
                                        </Tooltip>
                                    </Box>
                                </TableCell>
                            </TableRow>
                        );
                    })}
                </TableBody>
            </Table>
        </TableContainer>
    );
}